/**
 * Runtime (per-deployment) public config bridge.
 *
 * WHY THIS EXISTS — the ladder needs one promotable image.
 * Next.js inlines a *literal* `process.env.NEXT_PUBLIC_FOO` into the client bundle at
 * BUILD time. That bakes one deployment's values into the bytes, so the same image
 * cannot be promoted dev -> stg -> demo. Until now this repo rebuilt per environment
 * with `--build-arg`, which is why staging and prod ran genuinely different images.
 *
 * The fix, mirroring `nolia-country/frontend/src/config/runtime-config.ts`:
 *   - The server resolves these values per-request from the LIVE container env, using a
 *     COMPUTED key (`process.env[`NEXT_PUBLIC_${k}`]`). A computed key is NOT inlined,
 *     so it reads whatever the container was started with.
 *   - The root layout is `force-dynamic` and serialises the result into
 *     `window.__NOLIA_RUNTIME_CONFIG__`, because the browser cannot read the env.
 *   - Client code reads it back through `readRuntimeConfig()`.
 *
 * BACKWARDS COMPATIBLE — Ngāi Tahu is unaffected.
 * `frontend/Dockerfile` sets `ENV NEXT_PUBLIC_*` (not just `ARG`), so a baked image
 * carries those values in its runtime env as well. The server therefore resolves the
 * same values it always did. The build-arg path keeps working exactly as before; the
 * runtime path is additive. A deployment that injects the vars via Terraform simply
 * overrides the baked default.
 *
 * Resolution order: live container env -> build-time inlined value -> safe default.
 */

/**
 * Computed-key env read. The template literal is load-bearing: writing
 * `process.env.NEXT_PUBLIC_X` here instead would re-introduce build-time inlining and
 * silently undo this whole module.
 */
const getEnv = (key: string, fallback = ''): string =>
  process.env[`NEXT_PUBLIC_${key}`] ?? fallback;

export type NoliaVariant = 'procurement' | 'funding';

export interface RuntimeConfig {
  /** Which product surface to render. Wrong value = wrong KB categories, nav and routes. */
  variant: NoliaVariant;
  /** Client identifier threaded to Numa as `metadata.client_name`. */
  clientName: string;
  /** Staging-only cost column on the funding activity table. */
  costPreviewEnabled: boolean;
  /** Staging-only generated-rules inspector on the KB detail page. */
  rulesPreviewEnabled: boolean;
}

/** Window global the server injects and the client reads back. */
export const RUNTIME_CONFIG_WINDOW_KEY = '__NOLIA_RUNTIME_CONFIG__';

/**
 * The fallback used when nothing resolves. `procurement` matches the historic default in
 * `resolveVariant()` and the backend's `getInstanceConfig()`. Changing it must be done in
 * lockstep across all three, so it stays wrong-but-consistent rather than silently split.
 */
const DEFAULTS: RuntimeConfig = {
  variant: 'procurement',
  clientName: 'ngaitahu',
  costPreviewEnabled: false,
  rulesPreviewEnabled: false,
};

const asVariant = (raw: string): NoliaVariant | null =>
  raw === 'funding' || raw === 'procurement' ? raw : null;

/** Server-side: resolve from the live container env (evaluated per-request). */
export function resolveRuntimeConfig(): RuntimeConfig {
  const rawVariant = getEnv('NOLIA_VARIANT');
  const variant = asVariant(rawVariant);
  if (!variant) {
    // Warn on BOTH unset and unknown — a silently-wrong variant desyncs KB categories and
    // validation between tiers, and it fails quietly rather than loudly.
    console.warn(
      `NEXT_PUBLIC_NOLIA_VARIANT is ${rawVariant === '' ? 'unset' : `"${rawVariant}"`}; defaulting to "${DEFAULTS.variant}"`,
    );
  }
  return {
    variant: variant ?? DEFAULTS.variant,
    clientName: getEnv('CLIENT_NAME', DEFAULTS.clientName),
    costPreviewEnabled: getEnv('COST_PREVIEW_ENABLED') === 'true',
    rulesPreviewEnabled: getEnv('RULES_PREVIEW_ENABLED') === 'true',
  };
}

/**
 * Read the runtime config from anywhere.
 *   - Server (SSR): resolve straight from the live env.
 *   - Client: read what the server injected into `window`.
 * Both sides resolve from the same source, so SSR and hydration compute identical values
 * and a page that branches on one of them cannot produce a hydration mismatch.
 */
export function readRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return resolveRuntimeConfig();
  const injected = (window as unknown as Record<string, RuntimeConfig | undefined>)[
    RUNTIME_CONFIG_WINDOW_KEY
  ];
  return injected ?? DEFAULTS;
}
